// A single list of the properties a mortgage/lien can be attached to, drawn
// from data already in the loan file:
//   - each borrower's OWNED current residence (Applicant Info)
//   - every Investment REO property (including the refi subject property)
//
// Each entry has a STABLE key so a debt can reference it and the exporter can
// map it to the matching OWNED_PROPERTY asset label:
//   "res:<borrowerIndex>"  -> the current residence
//   "reo:<propertyIndex>"  -> an investment/REO property
//
// The same key scheme is read back on import from the
// ASSET_IsAssociatedWith_LIABILITY arc.

import { OWN } from "./renting_or_owning";
import { YES } from "./yes_no";

function residenceKey(b) {
  return [b.current_address, b.current_address_2, b.city, b.state, b.zip_code]
    .map(v => String(v || "").trim().toLowerCase())
    .join("|");
}

export function buildPropertyList(document) {
  const list = [];

  const borrowers =
    (document.applicant && document.applicant.borrowers) || [];

  // Borrowers who share a home (a married pair, or any of the up-to-4
  // borrowers at the same address) are ONE property, listed once — with every
  // owner named on it. The first borrower at an address owns the entry.
  const residenceByKey = {};
  borrowers.forEach((b, i) => {
    if (b.currently_renting_or_owning !== OWN || !b.current_address) return;
    const key = residenceKey(b);
    const name = `${b.first_name || ""} ${b.last_name || ""}`.trim();
    if (residenceByKey[key]) {
      if (name) residenceByKey[key].owners.push(name);
      return;
    }
    residenceByKey[key] = {
      key: `res:${i}`,
      label: b.current_address,
      owners: name ? [name] : []
    };
  });

  Object.values(residenceByKey).forEach(r => {
    list.push({
      key: r.key,
      label: r.label,
      sublabel: r.owners.length
        ? `Current residence — ${r.owners.join(", ")}`
        : "Current residence (Applicant Info)"
    });
  });

  const reo = document.investment_properties || [];
  reo.forEach((p, i) => {
    const isSubject = p.is_subject_property === YES;
    list.push({
      key: `reo:${i}`,
      label: p.property_address || `Property ${i + 1}`,
      sublabel: isSubject ? "Subject property" : `Investment REO #${i + 1}`
    });
  });

  return list;
}

/** Parse a property key into { type: "res"|"reo", index }. */
export function parsePropertyKey(key) {
  const m = /^(res|reo):(\d+)$/.exec(String(key || ""));
  if (!m) return null;
  return { type: m[1], index: parseInt(m[2], 10) };
}
